// import "./opentelemetry/local-trace";
import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { ModalStackProvider } from "@msys/ui";
import { Stack } from "@mui/material";
import * as Sentry from "@sentry/react";
import Keycloak from "keycloak-js";
import React from "react";
import { Inspector, InspectParams } from "react-dev-inspector";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { ApolloClientProvider } from "../clients/ApolloClientProvider.js";
import { suppressConsoleWarnings } from "../common/console.js";
import { FeatureProvider } from "../common/FeatureFlags.js";
import { initGoogleTagManager } from "../common/googleTagManager.js";
import { MuiThemeProvider } from "../common/MuiThemeProvider.js";
import { initSentry } from "../common/sentry.js";
import { renderBootstrapError } from "../common/bootstrap-error.js";
import { SnackbarProvider } from "../common/SnackbarProvider.js";
import { SplashScreen } from "../common/SplashScreen.js";
import "../common/syncfusion.js";
import { TranslationsProvider } from "../common/translations/TranslationsProvider.js";
import { KEYCLOAK_AUTH_SERVER, KEYCLOAK_REALM } from "../environment.js";
import { loadRuntimeConfig, type RuntimeConfig } from "../runtimeConfig.js";
import { AuthProvider } from "./auth/AuthProvider.js";
import { AvailableUsersProvider } from "./auth/AvailableUsersProvider.js";
import { SelectedUserProvider } from "./auth/SelectedUserProvider.js";
import { UserDataProvider } from "./auth/UserDataProvider.js";
import { ClipboardProvider } from "./commons/hooks/useClipboard.js";
import { BrandingProvider } from "./features/branding/context.js";
import { GlobalLoadingBar } from "./global-loading-state/GlobalLoadingBar.js";
import { GlobalLoadingStateProvider } from "./global-loading-state/GlobalLoadingStateProvider.js";
import { DeployCompatibility } from "./global/DeployCompatibility.js";
import { NativeFeatureProvider } from "./global/NativeFeatureProvider.js";
import { OutdatedAppVersionProvider } from "./global/OutdatedAppVersionProvider.js";
import { PushNotificationsProvider } from "./global/PushNotificationsProvider.js";
import { Router } from "./global/Router.js";
import { ScreenFeatureProvider } from "./global/ScreenFeatureProvider.js";
import { SidebarProvider } from "./global/SidebarProvider.js";
import "./index.css.js";
import { GoogleMapsProvider } from "./google-maps/GoogleMapsProvider.js";
import "../common/mui.js";
import { InvitationTokenProvider } from "./invitation/InvitationTokenProvider.js";
import { AppRoutes } from "./Routes.js";

suppressConsoleWarnings();

// Some legacy/CommonJS dependencies (e.g. Capacitor plugins, fbjs) reach for
// `globalThis.global` which exists in Node but not in browsers. See
// https://github.com/facebook/fbjs/issues/290
declare global {
  interface Window {
    global: Window & typeof globalThis;
  }
}
window.global = window;

interface AppProps {
  keycloak: Keycloak;
}

const App = ({ keycloak }: AppProps) => {
  // Remove initial loader
  React.useLayoutEffect(() => {
    document.querySelector("#app-loader")?.remove();
  }, []);

  return (
    <React.Suspense fallback={<SplashScreen />}>
      <HelmetProvider>
        <GoogleMapsProvider>
          <TranslationsProvider>
            <MuiThemeProvider>
              <FeatureProvider>
                <ScreenFeatureProvider>
                  <NativeFeatureProvider>
                    <AuthProvider keycloak={keycloak}>
                      <SelectedUserProvider>
                        <Router>
                          <SnackbarProvider>
                            <OutdatedAppVersionProvider>
                              <ApolloClientProvider>
                                <AvailableUsersProvider>
                                  <UserDataProvider>
                                    <ClipboardProvider>
                                      <ModalStackProvider>
                                        <BrandingProvider>
                                          <PushNotificationsProvider>
                                            <InvitationTokenProvider>
                                              <GlobalLoadingStateProvider>
                                                <GlobalLoadingBar />
                                                <SidebarProvider>
                                                  <Stack
                                                    direction={"row"}
                                                    sx={{
                                                      // Preventing top bar from being hidden on mobile
                                                      // when scrolling down more than one fold of the screen
                                                      height: {
                                                        xs: "unset",
                                                        md: "100%",
                                                      },
                                                      minHeight: {
                                                        xs: "100%",
                                                        md: "unset",
                                                      },
                                                    }}
                                                  >
                                                    <AppRoutes />
                                                  </Stack>
                                                </SidebarProvider>
                                              </GlobalLoadingStateProvider>
                                            </InvitationTokenProvider>
                                            <DeployCompatibility />
                                          </PushNotificationsProvider>
                                        </BrandingProvider>
                                      </ModalStackProvider>
                                    </ClipboardProvider>
                                  </UserDataProvider>
                                </AvailableUsersProvider>
                              </ApolloClientProvider>
                            </OutdatedAppVersionProvider>
                          </SnackbarProvider>
                        </Router>
                      </SelectedUserProvider>
                    </AuthProvider>
                  </NativeFeatureProvider>
                </ScreenFeatureProvider>
              </FeatureProvider>
            </MuiThemeProvider>
          </TranslationsProvider>
        </GoogleMapsProvider>
      </HelmetProvider>
    </React.Suspense>
  );
};

async function bootstrap(): Promise<void> {
  // Initialise Sentry early with no runtime config so config-load errors are
  // captured. environment is "bootstrap" until full config arrives.
  initSentry({
    initialScope: Capacitor.isNativePlatform()
      ? { tags: { capacitor: true } }
      : undefined,
  });

  let runtimeConfig: RuntimeConfig;
  try {
    runtimeConfig = await loadRuntimeConfig();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { phase: "bootstrap", source: "loadRuntimeConfig" },
    });
    renderBootstrapError(error);
    return;
  }

  // Re-initialise Sentry now that runtime config is available — attaches the
  // real environment / replay sample rates. Re-init replaces the bootstrap-stage
  // config; events captured between this call and the previous one (none in
  // the success path) are tagged with environment="bootstrap" and remain
  // findable in Sentry.
  initSentry({
    runtimeConfig,
    initialScope: Capacitor.isNativePlatform()
      ? { tags: { capacitor: true } }
      : undefined,
  });

  initGoogleTagManager(runtimeConfig.VITE_DEPLOY_ENV);

  if (Capacitor.isNativePlatform()) {
    CapacitorUpdater.notifyAppReady();
  }

  const keycloak = new Keycloak({
    url: KEYCLOAK_AUTH_SERVER,
    realm: KEYCLOAK_REALM,
    clientId: runtimeConfig.VITE_KEYCLOAK_CLIENT,
  });

  const InspectorWrapper =
    !Capacitor.isNativePlatform() && runtimeConfig.VITE_DEPLOY_ENV === "local"
      ? Inspector
      : React.Fragment;

  const appRoot = document.getElementById("app-root");
  if (appRoot === null) {
    throw new Error('Could not find root element "#app-root"');
  }
  const root = createRoot(appRoot);
  root.render(
    <>
      <InspectorWrapper
        // props docs see:
        // https://github.com/zthxxx/react-dev-inspector#inspector-component-props
        keys={["control", "shift", "command", "c"]}
        disableLaunchEditor={false}
        onHoverElement={(params: InspectParams) => {}}
        onClickElement={(params: InspectParams) => {}}
      >
        <App keycloak={keycloak} />
      </InspectorWrapper>
    </>
  );
}

void bootstrap().catch(error => {
  Sentry.captureException(error, {
    tags: { phase: "bootstrap", source: "bootstrap" },
  });
  renderBootstrapError(error);
});
